import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import api from '../../api/api'

import { escalationTimerOptions, emergencyOrderLabels, buildEmergencyOrder } from './helper'
import EscalationSettings from './EscalationSettings'
import EmergencyOrder from './EmergencyOrder'

const EscalationsTab = ({
  escalationSettings,
  staffPeople,
  propertyManagers,
  shiftManagers,
  inboxId,
  priorities,
}) => {
  const { t } = useTranslation(['on-call-schedule', 'common'])
  const priorityLevelsOptions = useMemo(
    () =>
      escalationSettings.priorityLevels && escalationSettings.priorityLevels.length
        ? escalationSettings.priorityLevels.map((item) => ({ value: item, label: item }))
        : [],
    [escalationSettings.priorityLevels],
  )
  const [selectedTimerOption, setSelectedTimerOption] = useState(escalationTimerOptions[1])
  const [isAfterBusinessHours, setAfterBusinessHours] = useState(false)
  const [selectedPriorityLevels, setSelectedPriorityLevels] = useState(priorityLevelsOptions)
  const { displaySnackbar } = useSnackbar()

  const maxMiddleLevels = emergencyOrderLabels.length - 3
  const [emergencyOrder, setEmergencyOrder] = useState(() =>
    buildEmergencyOrder(0, selectedTimerOption.value),
  )

  useEffect(() => {
    let timerValue = selectedTimerOption.value

    if (escalationSettings.timer) {
      const timerOption =
        escalationTimerOptions.find((option) => option.value === escalationSettings.timer) ||
        escalationTimerOptions[1]
      setSelectedTimerOption(timerOption)
      timerValue = timerOption.value
    }

    setAfterBusinessHours(!!escalationSettings.onlyAfterBH)

    if (escalationSettings.responders) {
      const sortedResponders = [...escalationSettings.responders].sort(
        (a, b) => a.position - b.position,
      )

      const preparedPrevOrder = sortedResponders.map((item, index) => {
        let label = ''
        if (item.position === 0) {
          label = 'first'
        } else if (index === sortedResponders.length - 1) {
          label = 'last'
        } else {
          const labelIndex = item.position + 1
          label = emergencyOrderLabels[labelIndex]
        }

        const user = item.user
          ? {
              avatar: item.user.avatar || '',
              value: item.user.email || '',
              label: `${item.user.firstName} ${item.user.lastName}`,
            }
          : { avatar: '', value: '', label: '' }

        return {
          label,
          time: 0,
          deletable: label !== 'first' && label !== 'last',
          user,
          call: {
            attempt: item.callAttempts || 3,
            isEnabled: item.notifyCall || false,
          },
          sms: item.notifySMS || false,
          email: item.notifyEmail || false,
          push: item.notifyPush || false,
          contactType: item.externalPhoneNumber ? 'external' : 'internal',
          phoneNumber: item.externalPhoneNumber || '',
        }
      })

      const levelsCount = preparedPrevOrder.filter((item) => item.deletable).length

      const newEmergencyOrder = buildEmergencyOrder(levelsCount, timerValue, preparedPrevOrder)

      setEmergencyOrder(newEmergencyOrder)
    }
  }, [])

  const handleAddLevel = () => {
    setEmergencyOrder((prevOrder) => {
      const currentMiddleLevels = prevOrder.length - 3

      if (currentMiddleLevels >= maxMiddleLevels) {
        displaySnackbar(t('on-call-schedule:escalations.maxLevelsWarning'), true)
        return prevOrder
      }

      const newLevelsCount = currentMiddleLevels + 1
      const newEmergencyOrder = buildEmergencyOrder(
        newLevelsCount,
        selectedTimerOption.value,
        prevOrder,
      )
      return newEmergencyOrder
    })
  }

  const handleRemoveLevel = (indexToRemove) => {
    setEmergencyOrder((prevOrder) => {
      const itemToRemove = prevOrder[indexToRemove]

      if (!itemToRemove.deletable) {
        return prevOrder
      }

      const newOrder = prevOrder.filter((_, index) => index !== indexToRemove)

      const currentMiddleLevels = newOrder.length - 3
      const newEmergencyOrder = buildEmergencyOrder(
        currentMiddleLevels,
        selectedTimerOption.value,
        newOrder,
      )
      return newEmergencyOrder
    })
  }

  const handleUpdateLevel = (index, updatedProperties) => {
    setEmergencyOrder((prevOrder) => {
      const newOrder = [...prevOrder]
      const currentItem = newOrder[index]

      if (currentItem.label === 'first' && updatedProperties.user) {
        return prevOrder
      }

      newOrder[index] = { ...currentItem, ...updatedProperties }
      return newOrder
    })
  }

  useEffect(() => {
    setEmergencyOrder((prevOrder) =>
      buildEmergencyOrder(prevOrder.length - 3, selectedTimerOption.value, prevOrder),
    )
  }, [selectedTimerOption])

  const handleSave = () => {
    const escalationEnabled = true
    const timer = selectedTimerOption.value
    const onlyAfterBH = isAfterBusinessHours
    const priorityLevels = selectedPriorityLevels.map((level) => level.value || level)

    const responders = emergencyOrder
      .filter((_, index) => index)
      .map((item, index) => {
        const position = index

        let responder = {
          position,
          user: null,
          notifyCall: item.call ? item.call.isEnabled : false,
          callAttempts: item.call ? item.call.attempt : 0,
          notifyEmail: item.email || false,
          notifyPush: item.push || false,
          notifySMS: item.sms || false,
          externalPhoneNumber: null,
        }

        if (item.contactType === 'external') {
          responder.externalPhoneNumber = item.phoneNumber ? +item.phoneNumber : ''
        } else {
          if (item.user && item.user.value && item.user.value !== 'on-call') {
            responder.user = item.user.value
          }
        }

        return responder
      })

    const dataToSend = {
      escalationEnabled,
      timer,
      onlyAfterBH,
      priorityLevels,
      responders,
    }

    api
      .updateEscalationSettings(inboxId, dataToSend)
      .then(() => displaySnackbar(t('on-call-schedule:notifications.savedSuccessfully')))
      .catch(() => displaySnackbar(t('on-call-schedule:notifications.saveError'), true))
  }

  return (
    <div className="on-call-schedule__tab-content on-call-schedule__escalations-tab">
      <EscalationSettings
        selectedTimerOption={selectedTimerOption}
        setSelectedTimerOption={setSelectedTimerOption}
        isAfterBusinessHours={isAfterBusinessHours}
        setAfterBusinessHours={setAfterBusinessHours}
        priorityLevelsOptions={priorities}
        selectedPriorityLevels={selectedPriorityLevels}
        setSelectedPriorityLevels={setSelectedPriorityLevels}
      />
      <EmergencyOrder
        emergencyOrder={emergencyOrder}
        addLevel={handleAddLevel}
        removeLevel={handleRemoveLevel}
        updateLevel={handleUpdateLevel}
        staffPeople={staffPeople}
        propertyManagers={propertyManagers}
        shiftManagers={shiftManagers}
      />
      <div className="on-call-schedule__buttons" style={{ paddingRight: '40px' }}>
        <button onClick={handleSave} className="on-call-schedule__buttons--submit">
          {t('common:actions.save')}
        </button>
      </div>
    </div>
  )
}

EscalationsTab.propTypes = {
  shiftManagers: PropTypes.array,
  propertyManagers: PropTypes.array,
  staffPeople: PropTypes.array,
  escalationSettings: PropTypes.object,
  inboxId: PropTypes.string,
  priorities: PropTypes.array,
}

export default EscalationsTab
